import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ArrowLeft, 
  PlayCircle, 
  Clock, 
  CheckCircle2,
  FileText,
  Award,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Download,
  Lock,
  LockOpen,
  Settings
} from "lucide-react";
import { toast } from "sonner";
import { useAdminPermissions } from "@/hooks/useAdminPermissions";
import { useTrainingCompletion } from "@/hooks/useTrainingCompletion";
import { AdminTrainingEditor } from "./components/AdminTrainingEditor";
import { PresentationViewer } from "./components/PresentationViewer";
import { AssessmentForm } from "./components/AssessmentForm";
import { AssessmentSelector } from "./components/AssessmentSelector";
import { PresentationComponent } from "./components/PresentationComponent";

interface TrainingModule {
  id: string;
  title_en: string;
  title_el: string;
  description_en: string;
  description_el: string;
  content_en: any;
  content_el: any;
  duration_minutes: number;
  difficulty_level: string;
  category_id?: string;
  training_categories?: {
    id: string;
    name_en: string;
    name_el: string;
    color: string;
    icon: string;
  };
  is_active: boolean;
}

interface UserProgress {
  id: string;
  user_id: string;
  training_topic_id: string;
  progress_percentage: number;
  started_at: string;
  completed_at: string | null;
  last_accessed_at: string;
  status: string;
  total_time_spent_seconds: number;
  created_at: string;
  updated_at: string;
}

const TrainingModule = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isAdmin } = useAdminPermissions();
  const { completeTraining, isGeneratingCertificate } = useTrainingCompletion();
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const [startTime] = useState(new Date());
  const [module, setModule] = useState<TrainingModule | null>(null);
  const [sections, setSections] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [userProgress, setUserProgress] = useState([]);
  const [showAdminEditor, setShowAdminEditor] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentSection, setCurrentSection] = useState(0);
  const [expandedSection, setExpandedSection] = useState<number | null>(null);
  const [activeComponent, setActiveComponent] = useState<{type: string, item: any, index: number} | null>(null);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [completedSections, setCompletedSections] = useState<string[]>([]);
  const [allSlidesViewed, setAllSlidesViewed] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<'overview' | 'content' | 'materials' | 'assessment'>('overview');
  const [moduleCompleted, setModuleCompleted] = useState(false);
  const [finalScore, setFinalScore] = useState<number | null>(null);

  useEffect(() => {
    if (id) {
      fetchTrainingData();
      // Auto-open admin editor if admin=true in URL
      if (isAdmin && searchParams.get('admin') === 'true') {
        setShowAdminEditor(true);
      }
    }
  }, [id, isAdmin]);

  // Load user progress
  useEffect(() => {
    const loadProgress = async () => {
      if (!module?.id) return;
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      const { data: progress, error } = await supabase
        .from('user_module_progress')
        .select('progress_percentage, completed_sections')
        .eq('user_id', user.id)
        .eq('module_id', module.id)
        .single();
      
      console.log('Loaded progress:', { progress, error });
      
      if (progress) {
        // Filter out null values from the array
        const cleanedSections = (progress.completed_sections || []).filter((s: any) => s !== null && s !== undefined);
        setProgressPercentage(progress.progress_percentage || 0);
        setCompletedSections(cleanedSections);
        console.log('Progress state updated:', { 
          progressPercentage: progress.progress_percentage, 
          completedSections: cleanedSections 
        });
        console.log('Materials completed?', cleanedSections.includes('materials'));
        console.log('Completed sections array:', cleanedSections);
      }
    };
    
    loadProgress();
  }, [module?.id]);

  const fetchTrainingData = async () => {
    try {
      setLoading(true);
      
      // Fetch training module
      const { data: moduleData, error: moduleError } = await supabase
        .from('training_modules')
        .select('*, training_categories(*)')
        .eq('id', id)
        .maybeSingle();

      if (moduleError) throw moduleError;
      setModule(moduleData as any);

      // Fetch training sections
      const { data: sectionsData, error: sectionsError } = await supabase
        .from('training_sections')
        .select('*')
        .eq('module_id', id)
        .order('order_index');

      if (sectionsError) throw sectionsError;
      setSections(sectionsData || []);

      // Fetch training materials
      const { data: materialsData, error: materialsError } = await supabase
        .from('training_materials')
        .select('*')
        .eq('training_topic_id', id);

      if (materialsError) throw materialsError;
      setMaterials(materialsData || []);

      // Fetch user progress if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: progressData, error: progressError } = await supabase
          .from('user_section_progress')
          .select('section_id, completed_at, view_count')
          .eq('module_id', id)
          .eq('user_id', user.id);

        if (!progressError) {
          setUserProgress(progressData || []);
        }
      }

    } catch (error) {
      console.error('Error fetching training data:', error);
      toast.error('Failed to load training module');
    } finally {
      setLoading(false);
    }
  };

  const updateProgress = async (percentage: number, sectionName: string, score?: number) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !module?.id) {
        console.error('Missing user or module ID');
        return;
      }
      
      // Add section to completed list
      const newCompletedSections = [...completedSections.filter(s => s), sectionName]; // Filter out nulls
      console.log('Updating progress:', { percentage, sectionName, newCompletedSections, score });
      
      // Prepare update data
      const updateData: any = {
        user_id: user.id,
        module_id: module.id,
        progress_percentage: percentage,
        completed_sections: newCompletedSections,
        last_accessed: new Date().toISOString()
      };
      
      // Add final_score if provided
      if (score !== undefined) {
        updateData.final_score = score;
      }
      
      // Update database
      const { error } = await supabase
        .from('user_module_progress')
        .upsert(updateData, {
          onConflict: 'user_id,module_id'
        });
      
      console.log('Saved to database:', { newCompletedSections, score });
      
      if (error) {
        console.error('Error saving progress:', error);
        throw error;
      }
      
      // Update local state
      setProgressPercentage(percentage);
      setCompletedSections(newCompletedSections);
      console.log('Progress updated successfully. Completed sections:', newCompletedSections);
      
      toast.success(`Progress: ${percentage}% complete`);
    } catch (error) {
      console.error('Error updating progress:', error);
      toast.error('Failed to update progress');
    }
  };

  const startQuiz = () => {
    setActiveComponent({ type: 'assessmentSelector', item: null, index: -1 });
  };

  const handleSelectAssessment = (formId: string) => {
    setActiveComponent({ type: 'assessment', item: { id: formId }, index: -1 });
  };

  const handleStartSection = async (section: any) => {
    // Check if user can access this section (sequential access)
    const sectionIndex = sections.findIndex(s => s.id === section.id);
    const canAccess = await checkSectionAccess(sectionIndex);
    
    if (!canAccess) {
      toast.error(language === 'en' 
        ? 'You must complete previous sections first' 
        : 'Πρέπει να ολοκληρώσετε τις προηγούμενες ενότητες πρώτα');
      return;
    }

    setActiveComponent({ type: 'presentation', item: section, index: sectionIndex });
  };

  const checkSectionAccess = async (sectionIndex: number): Promise<boolean> => {
    if (sectionIndex === 0) return true; // First section is always accessible
    
    // Check if all previous sections are completed
    for (let i = 0; i < sectionIndex; i++) {
      const prevSection = sections[i];
      const completed = userProgress.find(p => p.section_id === prevSection.id);
      if (!completed) return false;
    }
    return true;
  };

  const markSectionComplete = async (sectionId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('user_section_progress')
        .upsert({
          user_id: user.id,
          section_id: sectionId,
          module_id: id,
          completed_at: new Date().toISOString()
        });

      if (error) throw error;
      
      // Refresh progress
      fetchTrainingData();
      toast.success(language === 'en' ? 'Section completed!' : 'Ενότητα ολοκληρώθηκε!');
    } catch (error) {
      console.error('Error marking section complete:', error);
    }
  };

  const handleCompleteContent = async (score?: number, passed?: boolean) => {
    if (activeComponent && activeComponent.type === 'presentation') {
      markSectionComplete(activeComponent.item.id);
      // Mark that all slides have been viewed
      setAllSlidesViewed(true);
      setActiveTab('content');
    }
    
    // Handle quiz completion
    if (activeComponent && activeComponent.type === 'assessment' && score !== undefined && id) {
      setFinalScore(score);
      // Mark module as 100% complete
      await updateProgress(100, 'assessment', score);
      setModuleCompleted(true);
      
      // Calculate time spent
      const timeSpentMinutes = Math.round((Date.now() - startTime.getTime()) / 60000);
      
      // Complete training and generate certificate
      try {
        await completeTraining({
          moduleId: id,
          quizScore: score,
          timeSpentMinutes
        });
      } catch (error) {
        console.error('Error completing training:', error);
      }
      
      toast.success(
        language === 'en' 
          ? `Quiz completed! Your score: ${score}%` 
          : `Quiz ολοκληρώθηκε! Βαθμολογία: ${score}%`
      );
    }
    
    setActiveComponent(null);
  };

  const previewMaterial = async (material: any) => {
    try {
      // Debug logging
      console.log('Material data:', material);
      
      // Check if file_url exists
      if (!material.file_url) {
        throw new Error('No file URL found for this material');
      }
      
      console.log('File URL:', material.file_url);
      
      // Determine which bucket to use based on file type
      const fileType = material.file_type?.toLowerCase();
      const bucket = fileType === 'video' ? 'training-videos' : 'training-materials';
      
      console.log('Using bucket:', bucket, 'for file type:', fileType);
      
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(material.file_url, 3600);
      
      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Signed URL generated:', data?.signedUrl ? 'SUCCESS' : 'FAILED');
      
      if (data?.signedUrl) {
        // Try multiple approaches to open the file
        const finalUrl = fileType === 'pdf' || fileType === 'document' || fileType === 'presentation'
          ? `https://docs.google.com/viewer?url=${encodeURIComponent(data.signedUrl)}&embedded=true`
          : data.signedUrl;
        
        console.log('Final URL:', finalUrl);
        
        // Method 1: Try direct window.open (most reliable)
        const newWindow = window.open(finalUrl, '_blank', 'noopener,noreferrer');
        
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          // Popup was blocked, try fallback
          console.warn('Popup blocked, trying fallback method');
          const link = document.createElement('a');
          link.href = finalUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        
        toast.success(language === 'en' ? 'Opening file...' : 'Άνοιγμα αρχείου...');
      } else {
        throw new Error('No signed URL generated');
      }
    } catch (error: any) {
      console.error('Error previewing material:', error);
      
      // Ultimate fallback: try to open in current tab
      try {
        if (!material.file_url) {
          throw new Error('No file URL available');
        }
        
        const fileType = material.file_type?.toLowerCase();
        const bucket = fileType === 'video' ? 'training-videos' : 'training-materials';
        const { data } = await supabase.storage
          .from(bucket)
          .createSignedUrl(material.file_url, 3600);
        
        if (data?.signedUrl) {
          // Copy to clipboard and show message
          await navigator.clipboard.writeText(data.signedUrl);
          
          // Try opening in current tab as last resort
          if (confirm(language === 'en' 
            ? 'Popup blocked. Open file in current tab?' 
            : 'Το popup μπλοκαρίστηκε. Άνοιγμα αρχείου στην τρέχουσα καρτέλα;')) {
            window.location.href = data.signedUrl;
          } else {
            toast.error(
              language === 'en' 
                ? 'Link copied to clipboard. You can paste it in a new tab.' 
                : 'Ο σύνδεσμος αντιγράφηκε στο clipboard. Μπορείτε να τον επικολλήσετε σε νέα καρτέλα.'
            );
          }
        }
      } catch (fallbackError) {
        toast.error(
          language === 'en' 
            ? `Failed to open file: ${error.message || 'Unknown error'}` 
            : `Αποτυχία ανοίγματος αρχείου: ${error.message || 'Άγνωστο σφάλμα'}`
        );
      }
    }
  };

  const downloadMaterial = async (material: any) => {
    try {
      // Check if file_url exists
      if (!material.file_url) {
        throw new Error('No file URL found for this material');
      }
      
      // Determine which bucket to use based on file type
      const fileType = material.file_type?.toLowerCase();
      const bucket = fileType === 'video' ? 'training-videos' : 'training-materials';
      
      const { data } = await supabase.storage
        .from(bucket)
        .createSignedUrl(material.file_url, 3600);
      
      if (data?.signedUrl) {
        // Create a temporary link to trigger download
        const link = document.createElement('a');
        link.href = data.signedUrl;
        
        // Set proper filename based on material title and file type
        // Handle both old (title) and new (title_en/title_el) field names
        const title = material.title_en || material.title_el || material.title || 'material';
        const filename = title.replace(/[^a-zA-Z0-9]/g, '_');
        
        // For videos and images, try to open in new tab, for others try to download
        if (fileType === 'video' || fileType === 'image') {
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
        } else {
          link.download = `${filename}.${fileType || 'file'}`;
        }
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success(
          fileType === 'video' || fileType === 'image'
            ? (language === 'en' ? 'Opening file...' : 'Άνοιγμα αρχείου...')
            : (language === 'en' ? 'Download started...' : 'Η λήψη ξεκίνησε...')
        );
      } else {
        throw new Error('No signed URL generated');
      }
    } catch (error: any) {
      console.error('Error downloading material:', error);
      toast.error(
        language === 'en' 
          ? `Failed to download: ${error.message || 'Unknown error'}` 
          : `Αποτυχία λήψης: ${error.message || 'Άγνωστο σφάλμα'}`
      );
    }
  };

  // Show completion screen if module is completed
  if (moduleCompleted && finalScore !== null) {
    return (
      <div className="container mx-auto p-6">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="p-12 text-center">
            <div className="mb-6">
              <CheckCircle2 className="h-24 w-24 mx-auto text-green-500" />
            </div>
            <h1 className="text-4xl font-bold mb-4">
              {language === 'en' ? 'Module Completed!' : 'Ολοκλήρωση Μονάδας!'}
            </h1>
            <div className="mb-6">
              <div className="text-6xl font-bold text-primary mb-2">{finalScore}%</div>
              <p className="text-xl text-muted-foreground">
                {language === 'en' ? 'Final Score' : 'Τελική Βαθμολογία'}
              </p>
            </div>
            <div className="space-y-4">
              <p className="text-lg">
                {language === 'en' 
                  ? 'Congratulations! You have successfully completed this training module.' 
                  : 'Συγχαρητήρια! Ολοκληρώσατε επιτυχώς αυτή την εκπαιδευτική μονάδα.'}
              </p>
              <div className="flex justify-center mt-8">
                <Button 
                  onClick={() => navigate('/training')}
                  size="lg"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {language === 'en' ? 'Back to Training' : 'Επιστροφή στην Εκπαίδευση'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If an active component is being shown, render it instead of the main content
  if (activeComponent) {
    const { type, item } = activeComponent;
    
    return (
      <div className="container mx-auto p-6">
        <div className="mb-6">
          <Button 
            variant="outline" 
            onClick={() => setActiveComponent(null)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {language === 'en' ? 'Back to Course' : 'Επιστροφή στο Μάθημα'}
          </Button>
        </div>
        
        {type === 'presentation' && (
          <PresentationViewer
            section={item}
            onComplete={() => handleCompleteContent()}
            onClose={() => setActiveComponent(null)}
          />
        )}
        
        {type === 'assessmentSelector' && (
          <AssessmentSelector
            moduleId={id!}
            onSelectAssessment={handleSelectAssessment}
            onClose={() => setActiveComponent(null)}
          />
        )}
        
        {type === 'assessment' && (
          <AssessmentForm
            formId={item.id}
            onComplete={(score, passed) => handleCompleteContent(score, passed)}
            onClose={() => setActiveComponent(null)}
          />
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Training module not found</h1>
          <Button onClick={() => navigate('/training')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Training
          </Button>
        </div>
      </div>
    );
  }

  const tabSections = [
    { id: 'overview', title: language === 'en' ? 'Overview' : 'Επισκόπηση' },
    { id: 'content', title: language === 'en' ? 'Learning Content' : 'Περιεχόμενο Μάθησης' },
    { id: 'materials', title: language === 'en' ? 'Materials' : 'Υλικά' },
    { id: 'assessment', title: language === 'en' ? 'Assessment' : 'Αξιολόγηση' }
  ];

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={() => navigate('/training')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {language === 'en' ? 'Back' : 'Πίσω'}
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              {language === 'en' ? module.title_en : module.title_el}
            </h1>
            <p className="text-muted-foreground">
              {language === 'en' ? module.description_en : module.description_el}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {module.training_categories && (
            <Badge variant="secondary">
              {language === 'en' ? module.training_categories.name_en : module.training_categories.name_el}
            </Badge>
          )}
          <Badge variant="outline">
            <Clock className="h-3 w-3 mr-1" />
            {module.duration_minutes} min
          </Badge>
        </div>
      </div>

      {/* Progress Bar */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              {language === 'en' ? 'Progress' : 'Πρόοδος'}
            </span>
            <span className="text-sm text-muted-foreground">
              {progressPercentage}%
            </span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </CardContent>
      </Card>

      {/* Content Tabs */}
      <Tabs
        value={activeTab} 
        onValueChange={(value: any) => {
          // Only allow tab change if not completed or if admin
          if (isAdmin || !moduleCompleted) {
            setActiveTab(value);
          }
        }} 
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview" disabled={moduleCompleted && !isAdmin}>
            {language === 'en' ? 'Overview' : 'Επισκόπηση'}
          </TabsTrigger>
          <TabsTrigger 
            value="content" 
            disabled={(moduleCompleted && !isAdmin) || (!completedSections.includes('learning_content') && activeTab !== 'content' && !isAdmin)}
          >
            {language === 'en' ? 'Content' : 'Περιεχόμενο'}
          </TabsTrigger>
          <TabsTrigger 
            value="materials" 
            disabled={(moduleCompleted && !isAdmin) || (!completedSections.includes('learning_content') && !isAdmin)}
          >
            {language === 'en' ? 'Materials' : 'Υλικό'}
          </TabsTrigger>
          <TabsTrigger 
            value="assessment" 
            disabled={moduleCompleted || (!completedSections.includes('materials') && !isAdmin)}
          >
            {language === 'en' ? 'Assessment' : 'Αξιολόγηση'}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <BookOpen className="h-5 w-5 mr-2" />
                {language === 'en' ? 'Course Overview' : 'Επισκόπηση Μαθήματος'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 border rounded-lg">
                  <Clock className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                  <p className="font-medium">{module.duration_minutes} {language === 'en' ? 'minutes' : 'λεπτά'}</p>
                  <p className="text-sm text-muted-foreground">{language === 'en' ? 'Duration' : 'Διάρκεια'}</p>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <Award className="h-8 w-8 mx-auto mb-2 text-green-600" />
                  <p className="font-medium">{language === 'en' ? 'Certificate' : 'Πιστοποιητικό'}</p>
                  <p className="text-sm text-muted-foreground">{language === 'en' ? 'Upon completion' : 'Μετά την ολοκλήρωση'}</p>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <FileText className="h-8 w-8 mx-auto mb-2 text-purple-600" />
                  <p className="font-medium">{language === 'en' ? 'Assessment' : 'Αξιολόγηση'}</p>
                  <p className="text-sm text-muted-foreground">{language === 'en' ? 'Required' : 'Απαιτείται'}</p>
                </div>
              </div>
              
              <div className="prose max-w-none">
                <h3>{language === 'en' ? 'What you will learn:' : 'Τι θα μάθετε:'}</h3>
                <ul>
                  <li>{language === 'en' ? 'Core concepts and principles' : 'Βασικές έννοιες και αρχές'}</li>
                  <li>{language === 'en' ? 'Practical applications' : 'Πρακτικές εφαρμογές'}</li>
                  <li>{language === 'en' ? 'Best practices and procedures' : 'Βέλτιστες πρακτικές και διαδικασίες'}</li>
                  <li>{language === 'en' ? 'Real-world scenarios' : 'Σενάρια πραγματικού κόσμου'}</li>
                </ul>
              </div>

              <Button 
                onClick={() => {
                  if (completedSections.includes('learning_content')) {
                    return; // Already completed
                  }
                  // Start the first section automatically
                  if (sections.length > 0) {
                    setActiveTab('content');
                    handleStartSection(sections[0]);
                  }
                }} 
                className="w-full"
                disabled={completedSections.includes('learning_content')}
              >
                {completedSections.includes('learning_content') ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    {language === 'en' ? 'Completed' : 'Ολοκληρώθηκε'}
                  </>
                ) : (
                  <>
                    <PlayCircle className="h-4 w-4 mr-2" />
                    {language === 'en' ? 'Start Training' : 'Έναρξη Εκπαίδευσης'}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="content" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{language === 'en' ? 'Learning Content' : 'Περιεχόμενο Μάθησης'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="prose max-w-none">
                <p>{language === 'en' ? module.description_en : module.description_el}</p>
                
                <h3>{language === 'en' ? 'Learning Sections:' : 'Ενότητες Μάθησης:'}</h3>
                <div className="space-y-4">
                  {sections.length === 0 ? (
                    <div className="text-center p-8 text-muted-foreground">
                      {isAdmin ? (
                        <div>
                          <p className="mb-4">
                            {language === 'en' 
                              ? 'No sections created yet. Use the admin panel to add content.'
                              : 'Δεν έχουν δημιουργηθεί ενότητες ακόμα. Χρησιμοποιήστε το panel διαχειριστή για να προσθέσετε περιεχόμενο.'
                            }
                          </p>
                          <Button onClick={() => setShowAdminEditor(true)}>
                            <Settings className="h-4 w-4 mr-2" />
                            {language === 'en' ? 'Open Admin Editor' : 'Άνοιγμα Editor Διαχειριστή'}
                          </Button>
                        </div>
                      ) : (
                        <p>
                          {language === 'en' 
                            ? 'No content available yet.'
                            : 'Δεν υπάρχει διαθέσιμο περιεχόμενο ακόμα.'
                          }
                        </p>
                      )}
                    </div>
                  ) : (
                    sections.map((section, index) => {
                      const isCompleted = userProgress.find(p => p.section_id === section.id);
                      const canAccess = index === 0 || userProgress.find(p => p.section_id === sections[index - 1]?.id);
                      
                      return (
                        <div key={section.id} className="border rounded-lg overflow-hidden">
                          <div className="p-4 bg-white">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="flex-shrink-0">
                                  {isCompleted ? (
                                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                                  ) : canAccess ? (
                                    <LockOpen className="h-5 w-5 text-blue-600" />
                                  ) : (
                                    <Lock className="h-5 w-5 text-gray-400" />
                                  )}
                                </div>
                                <div>
                                  <h4 className="font-medium">
                                    {language === 'en' ? section.title_en : section.title_el}
                                  </h4>
                                  <div className="flex items-center space-x-2 mt-1">
                                    <Badge variant="outline" className="text-xs">
                                      {section.section_type === 'lesson' ? '📖 Lesson' : '🛠️ Practical'}
                                    </Badge>
                                    {section.presentation_file_path && (
                                      <Badge variant="outline" className="text-xs">
                                        <FileText className="h-3 w-3 mr-1" />
                                        Presentation
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <Button
                                onClick={() => handleStartSection(section)}
                                disabled={!canAccess}
                                size="sm"
                              >
                                {isCompleted ? (
                                  <>
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                    {language === 'en' ? 'Review' : 'Επανεξέταση'}
                                  </>
                                ) : (
                                  <>
                                    <PlayCircle className="h-4 w-4 mr-2" />
                                    {language === 'en' ? 'Start' : 'Έναρξη'}
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                
              </div>
              
              {/* Complete Learning Session Button - Only shows after all slides viewed */}
              {allSlidesViewed && !completedSections.includes('learning_content') && (
                <div className="mt-6">
                  <Button 
                    onClick={() => updateProgress(33, 'learning_content')} 
                    className="w-full"
                    size="lg"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    {language === 'en' ? 'Complete Learning Session' : 'Ολοκλήρωση Συνεδρίας Μάθησης'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="materials" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{language === 'en' ? 'Training Materials' : 'Εκπαιδευτικό Υλικό'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {materials.length === 0 ? (
                <div className="text-center p-8 text-muted-foreground">
                  <p>
                    {language === 'en' 
                      ? 'No materials available yet.'
                      : 'Δεν υπάρχουν διαθέσιμα υλικά ακόμα.'
                    }
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {materials.map((material) => {
                    // Handle both old (title) and new (title_en/title_el) field names
                    const title = (language === 'en' ? material.title_en : material.title_el) || material.title || 'Untitled';
                    const description = (language === 'en' ? material.description_en : material.description_el) || material.description || '';
                    
                    return (
                      <div key={material.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">
                              {title}
                            </h4>
                            {description && (
                              <p className="text-sm text-muted-foreground">
                                {description}
                              </p>
                            )}
                            <Badge variant="outline" className="mt-2">
                              {material.file_type?.toUpperCase() || 'FILE'}
                            </Badge>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="default"
                              onClick={() => previewMaterial(material)}
                              disabled={!material.file_url}
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              {language === 'en' ? 'Preview' : 'Προεπισκόπηση'}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => downloadMaterial(material)}
                              disabled={!material.file_url}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              {language === 'en' ? 'Download' : 'Λήψη'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              <Button 
                onClick={async () => {
                  await updateProgress(66, 'materials');
                  // Switch to assessment tab and start quiz
                  setActiveTab('assessment');
                  setTimeout(() => {
                    startQuiz();
                  }, 300);
                }} 
                className="w-full"
                disabled={!completedSections.includes('learning_content') || completedSections.includes('materials')}
              >
                {completedSections.includes('materials') 
                  ? language === 'en' ? 'Completed' : 'Ολοκληρώθηκε'
                  : !completedSections.includes('learning_content') 
                    ? language === 'en' ? 'Complete Learning Content First' : 'Ολοκληρώστε πρώτα το μάθημα'
                    : language === 'en' ? 'Done With Reviewing Materials' : 'Ολοκλήρωση Εξέτασης Υλικών'
                }
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assessment" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{language === 'en' ? 'Final Assessment' : 'Τελική Αξιολόγηση'}</CardTitle>
              <CardDescription>
                {language === 'en' 
                  ? 'Complete the quiz to earn your certificate'
                  : 'Ολοκληρώστε το quiz για να κερδίσετε το πιστοποιητικό σας'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center p-6 border-2 border-dashed rounded-lg">
                <Award className="h-12 w-12 mx-auto mb-4 text-amber-500" />
                <h3 className="text-lg font-medium mb-2">
                  {language === 'en' ? 'Ready for Assessment?' : 'Έτοιμοι για Αξιολόγηση;'}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {language === 'en' 
                    ? 'Complete all sections above before taking the quiz'
                    : 'Ολοκληρώστε όλες τις ενότητες πάνω πριν προχωρήσετε στο quiz'
                  }
                </p>
                <Button 
                  onClick={startQuiz}
                  disabled={moduleCompleted || (!isAdmin && !completedSections.includes('materials'))}
                  size="lg"
                >
                  {moduleCompleted ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      {language === 'en' ? 'Quiz Completed' : 'Quiz Ολοκληρώθηκε'}
                    </>
                  ) : !completedSections.includes('materials') && !isAdmin ? (
                    <>
                      <Lock className="h-4 w-4 mr-2" />
                      {language === 'en' ? 'Complete Materials First' : 'Ολοκληρώστε τα υλικά πρώτα'}
                    </>
                  ) : (
                    language === 'en' ? 'Take Quiz' : 'Συμμετοχή στο Quiz'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Admin Editor Modal */}
      {isAdmin && showAdminEditor && id && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">
                  {language === 'en' ? 'Admin Training Editor' : 'Διαχείριση Εκπαιδευτικής Ενότητας'}
                </h2>
                <Button 
                  variant="outline" 
                  onClick={() => setShowAdminEditor(false)}
                >
                  {language === 'en' ? 'Close' : 'Κλείσιμο'}
                </Button>
              </div>
              <AdminTrainingEditor 
                moduleId={id} 
                onUpdate={() => {
                  fetchTrainingData();
                  setShowAdminEditor(false);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingModule;